import zustand from "zustand";
import { dayjs } from "./config";
import { LandCode, useDefaultValues } from "./defaultValues";
import { createEntityStore, UseEntityStore } from "./entityStore";
import { useRxJsSub } from "./utils";
import { BehaviorSubject, combineLatest } from "rxjs";
import { createAxios } from "./axios";
import { useAccountStore } from "./account";

interface InputNumberFormat {
    thousandSeparator: string;
    decimalSeparator: string;
    currencyDecimalPlaces: number;
}

export const numberFormatByLandCode: { [landCode in LandCode]: InputNumberFormat; } = {
    "CZ": { decimalSeparator: ",", thousandSeparator: " ", currencyDecimalPlaces: 2 },
    "PL": { decimalSeparator: ",", thousandSeparator: " ", currencyDecimalPlaces: 2 },
    "SK": { decimalSeparator: ",", thousandSeparator: " ", currencyDecimalPlaces: 2 },
    "HU": { decimalSeparator: ",", thousandSeparator: " ", currencyDecimalPlaces: 0 },
    "EE": { decimalSeparator: ",", thousandSeparator: " ", currencyDecimalPlaces: 2 },
    "LV": { decimalSeparator: ",", thousandSeparator: " ", currencyDecimalPlaces: 2 },
    "HR": { decimalSeparator: ",", thousandSeparator: " ", currencyDecimalPlaces: 2 },
    "SI": { decimalSeparator: ",", thousandSeparator: " ", currencyDecimalPlaces: 2 },
}

export const numberInputRegexByLandCode: { [landCode in LandCode]: RegExp; } = {
    "CZ": /^-?[\d ]+(?:,[\d ]*)?$/,
    "PL": /^-?[\d ]+(?:,[\d ]*)?$/,
    "SK": /^-?[\d ]+(?:,[\d ]*)?$/,
    "HU": /^-?[\d ]+(?:,[\d ]*)?$/,
    "EE": /^-?[\d ]+(?:,[\d ]*)?$/,
    "LV": /^-?[\d ]+(?:,[\d ]*)?$/,
    "HR": /^-?[\d .]+(?:,[\d ]*)?$/,
    "SI": /^-?[\d .]+(?:,[\d ]*)?$/,
}

export const timeFormatByLandCode: { [landCode in LandCode]: string; } = {
    "CZ": "H:mm:ss",
    "PL": "H:mm:ss",
    "SK": "H:mm:ss",
    "HU": "H:mm:ss",
    "EE": "HH:mm:ss",
    "LV": "H:mm:ss",
    "HR": "HH:mm:ss",
    "SI": "HH:mm:ss",
}

export const dateFormatByLandCode: { [landCode in LandCode]: string; } = {
    "CZ": "D. M. YYYY",
    "PL": "D. M. YYYY",
    "SK": "D. M. YYYY",
    "HU": "YYYY. MM. DD.",
    "EE": "D.M.YYYY",
    "LV": "D.MM.YYYY.",
    "HR": "DD. MM. YYYY.",
    "SI": "D. M. YYYY",
}

export const monthFormatByLandCode: { [landCode in LandCode]: string; } = {
    "CZ": "M. YYYY",
    "PL": "M. YYYY",
    "SK": "M. YYYY",
    "HU": "YYYY. MM.",
    "EE": "M.YYYY",
    "LV": "MM.YYYY.",
    "HR": "MM. YYYY.",
    "SI": "M. YYYY",
}

export const dateTimeFormatByLandCode: { [landCode in LandCode]: string; } = {
    "CZ": dateFormatByLandCode["CZ"] + " " + timeFormatByLandCode["CZ"],
    "PL": dateFormatByLandCode["PL"] + " " + timeFormatByLandCode["PL"],
    "SK": dateFormatByLandCode["SK"] + " " + timeFormatByLandCode["SK"],
    "HU": dateFormatByLandCode["HU"] + " " + timeFormatByLandCode["HU"],
    "EE": dateFormatByLandCode["EE"] + " " + timeFormatByLandCode["EE"],
    "LV": dateFormatByLandCode["LV"] + " " + timeFormatByLandCode["LV"],
    "HR": dateFormatByLandCode["HR"] + " " + timeFormatByLandCode["HR"],
    "SI": dateFormatByLandCode["SI"] + ", " + timeFormatByLandCode["SI"],
}

export const dateInputRegexByLandCode: { [landCode in LandCode]: RegExp; } = {
    "CZ": /^\d{1,2}\.\d{1,2}\.\d{4}$/,
    "PL": /^\d{1,2}\.\d{1,2}\.\d{4}$/,
    "SK": /^\d{1,2}\.\d{1,2}\.\d{4}$/,
    "HU": /^\d{1,2}\.\d{1,2}\.\d{4}\.?$/,
    "EE": /^\d{1,2}\.\d{1,2}\.\d{4}$/,
    "LV": /^\d{1,2}\.\d{1,2}\.\d{4}$/,
    "HR": /^\d{1,2}\.\d{1,2}\.\d{4}\.?$/,
    "SI": /^\d{1,2}\.\d{1,2}\.\d{4}$/,
}

export const monthInputRegexByLandCode: { [landCode in LandCode]: RegExp; } = {
    "CZ": /^\d{1,2}\.\d{4}$/,
    "PL": /^\d{1,2}\.\d{4}$/,
    "SK": /^\d{1,2}\.\d{4}$/,
    "HU": /^\d{4}\.\d{1,2}\.?$/,
    "EE": /^\d{1,2}\.\d{4}$/,
    "LV": /^\d{1,2}\.\d{4}$/,
    "HR": /^\d{1,2}\.\d{4}\.?$/,
    "SI": /^\d{1,2}\.\d{4}$/,
}

export function formatDate(value: string | null | undefined, landCode: LandCode) {
    if (value == null) {
        return "";
    }
    const format = dateFormatByLandCode[landCode];
    return dayjs.tz(value).format(format);
}

export function formatDateTime(value: string | null | undefined, landCode: LandCode) {
    if (value == null) {
        return "";
    }
    const format = dateTimeFormatByLandCode[landCode];
    return dayjs.tz(value).format(format);
}

export function formatNumber(value: number | null | undefined, landCode: LandCode, isCurrency: boolean, avoidZeroDecimals = false) {
    if (value == null) {
        return "";
    }
    const format = numberFormatByLandCode[landCode];
    const parts = value.toString(10).split(".");
    const left = parts[0];
    let right = parts[1];
    const leftFormatted = chunk(left, 3, true).join(format.thousandSeparator);
    if (isCurrency && format.currencyDecimalPlaces > 0 && (right == null || right.length < format.currencyDecimalPlaces)) {
        right = (right == null ? "" : right).padEnd(format.currencyDecimalPlaces, "0");
    }
    if (avoidZeroDecimals && right != null && right.replace(/0/g, "").length === 0) {
        right = "";
    }
    const rightFormatted = right == null ? "" : right;
    return leftFormatted + (rightFormatted === "" ? "" : format.decimalSeparator + rightFormatted);
}

export function parseNumber(text: string, landCode: LandCode): number | null {
    const regex = numberInputRegexByLandCode[landCode];
    if (text.trim() === "" || !regex.test(text)) {
        return null;
    }
    const format = numberFormatByLandCode[landCode];
    const parts = text.split(format.decimalSeparator);
    const left = parts[0]!;
    const right = parts[1];
    const leftFormatted = left.match(/[-\d]/g)!.join("");
    const rightFormatted = right == null || right.trim() === "" ? "" : right.match(/\d/g)!.join("");
    return Number.parseFloat(leftFormatted + "." + rightFormatted);
}

function chunk(str: string, size: number, fromRight: boolean) {
    const numChunks = Math.ceil(str.length / size);
    const chunks = new Array(numChunks);
    let i = 0, o = 0;
    if (fromRight) {
        const remainder = str.length % size;
        if (remainder > 0) {
            chunks[0] = str.substring(0, remainder);
            i = 1;
            o = remainder;
        }
    }
    for (; i < numChunks; ++i, o += size) {
        chunks[i] = str.substring(o, o + size);
    }
    return chunks;
}

export interface Resources { [name: string]: { [language: string]: string | boolean | number } }

export const useResourcesStore = createEntityStore<Resources>({ path: "common/resources", data: {} });

let newResourcesCount = 0;
const newResources: Resources = {};

export function getResource(name: string, defaultText: string | null = null, language = useLanguageStore.getState().language, resources = useResourcesStore.getState().clientData) {
    if (newResourcesCount > 0) {
        resources = { ...resources, ...newResources };
    }
    if (language == null) {
        return defaultText ?? name;
    }
    const value = resources[name];
    const text = value == null ? null : value[language];
    const defaultLangText = value == null ? null : value["en"];
    if (text == null && defaultLangText == null) {
        newResourcesCount++;
        newResources[name] = { "en": defaultText ?? name };
        setTimeout(async () => {
            useResourcesStore.getState().setClientDataFunc(resources => ({ ...resources, [name]: { "en": defaultText ?? name } }));
            try {
                await createAxios(false, false).post("common/resources/missingResource", { name, language: "en", stringValue: defaultText ?? name });
            } finally {
                newResourcesCount--;
                delete newResources[name];
            }
        });
    }
    return toNullIfEmpty(text) ?? toNullIfEmpty(defaultLangText) ?? defaultText ?? name;
}

export function toNullIfEmpty(value: string | boolean | number | null | undefined) {
    if (value == null) {
        return null;
    }
    if (typeof value === "string" && value === "") {
        return null;
    }
    return value;
}

export function getValidationText(key: string, defaultMessage?: string) {
    return getResource("Ui.Validation." + key, defaultMessage) as string;
}

export function getLocalizedCurrency(currency: string) {
    return getResource("Ui.Currency." + currency) as string;
}

export const useLanguageStore = zustand<{ language: string | undefined; setLanguage(language: string | undefined): void; language$: BehaviorSubject<string | undefined>; }>((set, get) => ({
    language: undefined,
    language$: new BehaviorSubject<string | undefined>(undefined),
    setLanguage(language: string | undefined) {
        set({ language });
        get().language$.next(language);
    },
}));

export function useResources() {
    const areResourcesFetched = useResourcesStore(p => p.isFetched);
    const isDefFetched = useDefaultValues(p => p.isFetched);
    useRxJsSub(() => combineLatest([useResourcesStore.getState().clientData$, useDefaultValues.getState().clientData$, useAccountStore.getState().data$]).subscribe({
        next([resources, def, account]) {
            if (!resources.isFetching && !def.isFetching) {
                const lang = account?.accountData.accountInfo.language;
                useLanguageStore.getState().setLanguage((lang ?? "") === "" ? getLanguageFromLandCode(def.data.landCode) : account?.accountData.accountInfo.language);
            }
        },
    }), () => {
        useLanguageStore.getState().setLanguage(undefined);
    });
    const language = useLanguageStore(p => p.language);
    return { ready: areResourcesFetched && isDefFetched && language != null };
}

export function useLocalizedPropValidators<T>(useStore: UseEntityStore<T>) {
    useRxJsSub(() => useLanguageStore.getState().language$.subscribe(() => {
        useStore.getState().onDataChanged(false);
    }));
}

export type Language = "en" | "cs" | "sk" | "lv" | "hu" | "hr" | "sl" | "et" | "pl";

export function getLanguageFromLandCode(landCode: LandCode): Language {
    switch (landCode) {
        case "CZ": return "cs";
        case "PL": return "pl";
        case "SK": return "sk";
        case "LV": return "lv";
        case "HU": return "hu";
        case "HR": return "hr";
        case "SI": return "sl";
        case "EE": return "et";
        default: return "en";
    }
}

export const allLanguages = ["en", "cs", "sk", "lv", "hu", "hr", "sl", "et", "pl"];
